import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { BreadcrumbsComponent } from 'components/breadcrumbs/breadcrumbs.component';
import { ClientActivitiesComponent } from 'components/client-view-sections/client-activities/client-activities.component';
import { ClientNotificationsComponent } from 'components/client-view-sections/client-notifications/client-notifications.component';
import { GeneralInfoFormComponent } from 'components/client-view-sections/general-info-form/general-info-form.component';
import { LnaNotesFormComponent } from 'components/client-view-sections/lna-notes-form/lna-notes-form.component';
import { ReadersCardFormComponent } from 'components/client-view-sections/readers-card-form/readers-card-form.component';
import { ContentSideNavComponent } from 'components/content-side-nav/content-side-nav.component';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { TableComponent } from 'components/table/table.component';
import { API_SERVICE_REQUEST, ApiService } from 'services/api.service';
import { ToastService } from 'services/toast.service';
import { formatIdentityNumber } from 'util/user.utility';
import { Breadcrumb } from 'types/breadcrumb.interface';
import {
  ClientCard,
  ClientNoteListByClientIdItem,
  ClientNotificationByClientIdListItemPagedResponse,
  DeleteClientRequest,
  ReaderCardByClientId,
  ResearchTopicByClientIdCard,
  ResearchTopicByClientIdCardPagedResponse
} from 'types/client.interface';
import { CountryDto, CountryDtoPagedResult } from 'types/country.interface';
import { LegalEntityCard, LegalEntityFormOption } from 'types/legal-entity.interface';
import { FilterType, TableColumn } from 'types/table-column.interface';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { KEY_LAST_ACTIVE_ORG_SECTION } from 'pages/client-pages/view-organization-page/view-organization-page.component';

export const KEY_LAST_ACTIVE_SECTION = 'lastActiveSection';
export const DEFAULT_NOTES_PAGE_SIZE = 5;

@Component({
  selector: 'app-view-client-page',
  standalone: true,
  imports: [
    BreadcrumbsComponent,
    TranslateModule,
    ContentSideNavComponent,
    GeneralInfoFormComponent,
    LnaNotesFormComponent,
    ReadersCardFormComponent,
    SpinnerComponent,
    ClientActivitiesComponent,
    ClientNotificationsComponent,
    NgClass,
    TableComponent,
    ConfirmModalComponent
  ],
  templateUrl: './view-client-page.component.html',
  styleUrl: './view-client-page.component.scss'
})
export class ViewClientPageComponent implements OnInit {
  readonly clientApplicationsEndpoint = API_SERVICE_REQUEST;
  public readonly formatIdentityNumber = formatIdentityNumber;

  getServiceRequestViewPath = (row: any): string => {
    if (row.serviceRequestType === 'InformationRequest') {
      if (row.status === 'Melnraksts') {
        return `/pakalpojumi/informacijas-pieprasijums/${row.id}`;
      }
      return `/informacijas-pieprasijums/${row.id}`;
    }
    return `/pieteikums/${row.id}`;
  };

  breadcrumbs: Breadcrumb[] = [];
  isSubmittingNoteFiles: boolean = false;
  isClientLoading: boolean = true;
  isCountriesLoading: boolean = true;
  isUsersLoading: boolean = true;
  isResearchTopicsLoading: boolean = true;
  isClientNotificationsLoading: boolean = true;
  isClientNotesLoading: boolean = true;
  clientId?: string;
  client?: ClientCard;
  navItems: string[] = [];
  navItemsIndividual = [
    'general', 
    'readers_card', 
    'service_history', 
    'activities', 
    'notifications', 
    'notes'
  ];
  navItemsOrganization = [
    'general', 
    'activities', 
    'notifications', 
    'notes'
  ];
  activeItem: string = 'general';
  countries: CountryDto[] = [];
  clientNotifications?: ClientNotificationByClientIdListItemPagedResponse;
  clientNotes?: ClientNoteListByClientIdItem[];
  notificationPage: number = 1;
  notificationPageSize: number = 10;
  notesPage: number = 1;
  notesPageSize: number = DEFAULT_NOTES_PAGE_SIZE;
  notesCount: number = 0;
  initialActiveItem?: string = 'general';
  readersCard?: ReaderCardByClientId;
  researchTopics: ResearchTopicByClientIdCard[] = [];
  isReadersCardLoading: boolean = true;
  hasFormChanged: boolean = false;
  confirmModalOpen: boolean = false;
  nextSection: string = '';
  changeBack: boolean = false;
  legalEntity?: LegalEntityCard;
  legalEntityFormOptions: LegalEntityFormOption[] = [];

  get isLoading(): boolean {
    return (
      this.isClientLoading ||
      this.isCountriesLoading ||
      this.isReadersCardLoading ||
      this.isResearchTopicsLoading ||
      this.isClientNotificationsLoading
    );
  }

  appTableColumns: TableColumn[] = [
    {
      name: 'pages.client.serviceHistory.code',
      key: 'code',
      sortEnabled: true,
      sortKey: 'Code',
      filter: FilterType.Search
    },
    {
      name: 'pages.client.serviceHistory.serviceName',
      key: 'serviceName',
      sortEnabled: true,
      sortKey: 'ServiceName',
      multiselectFilterName: 'serviceRequestType',
      filter: FilterType.MultiSelect,
      filterOptions: ['ReadingRoomSubmission', 'InformationRequest']
    },
    {
      name: 'pages.client.serviceHistory.submittedOn',
      key: 'submittedOn',
      type: 'date',
      sortEnabled: true,
      sortKey: 'SubmittedOn',
      filter: FilterType.DateTime
    },
    {
      name: 'pages.client.serviceHistory.submitter',
      key: 'submitterFullName',
      sortEnabled: true,
      sortKey: 'SubmitterFullName',
      filter: FilterType.Search
    },
    {
      name: 'pages.client.serviceHistory.status',
      key: 'status',
      styleClass: 'status-column',
      sortEnabled: true,
      sortKey: 'Status',
      multiselectFilterName: 'statuses',
      filter: FilterType.MultiSelect,
      dynamicFilterOptionsField: 'status',
      dynamicFilterSourceField: 'statusCounts',
      dynamicFilterSourceIdKey: 'status',
      dynamicFilterSourceLabelKey: 'status'
    },
    {
      name: 'pages.client.serviceHistory.result',
      key: 'result',
      sortEnabled: true,
      sortKey: 'Result',
      filter: FilterType.Search
    }
  ];
  financialColumns: TableColumn[] = [
    {
      key: 'invoiceNumber',
      name: 'pages.client.tableColumns.invoiceNumber',
      sortEnabled: true
    },
    {
      key: 'serviceName',
      name: 'pages.client.tableColumns.serviceName',
      sortEnabled: true
    },
    {
      key: 'paymentDate',
      name: 'pages.client.tableColumns.paymentDate',
      sortEnabled: true
    },
    {
      key: 'amount',
      name: 'pages.client.tableColumns.amount',
      sortEnabled: true
    },
    {
      key: 'paymentStatus',
      name: 'pages.client.tableColumns.status',
      sortEnabled: true
    }
  ];
  serviceColumns: TableColumn[] = [
    {
      key: 'serviceNumber',
      name: 'pages.client.tableColumns.applicationNumber',
      sortEnabled: true
    },
    {
      key: 'serviceName',
      name: 'pages.client.tableColumns.serviceName',
      sortEnabled: true
    },
    {
      key: 'applicationDate',
      name: 'pages.client.tableColumns.applicationDate',
      sortEnabled: true
    },
    {
      key: 'status',
      name: 'pages.client.tableColumns.status',
      sortEnabled: true
    },
    {
      key: 'result',
      name: 'pages.client.tableColumns.result',
      sortEnabled: true
    }
  ];

  constructor(
    private apiService: ApiService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private toastService: ToastService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params.subscribe((params) => {
      this.clientId = params['id'];

      if (this.clientId) {
        this.getCountries();
        this.getResearchTopics();
        this.getClient();
        this.getReadersCard();
        this.getClientAllNotesByClientId();
      }
    });
  }

  getClientAllNotesByClientId(): void {
    this.apiService.getAllClientNotes(this.clientId!, this.notesPage, this.notesPageSize).subscribe(
      (data: ClientNoteListByClientIdItem[]) => {
        this.isClientNotificationsLoading = false;
        this.clientNotes = data;
        this.notesCount = data?.length ?? 0;
      },
      (error) => {
        console.error(error);
      }
    );
  }

  getNotes(): void {
    if (this.clientId) {
      this.apiService.getAllClientNotes(this.clientId!, this.notesPage, this.notesPageSize).subscribe(
        (data: ClientNoteListByClientIdItem[]) => {
          this.clientNotes = data;
          this.notesCount = data?.length ?? 0;
          this.isClientNotesLoading = false;
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }

  onNotesPageChange(page: number): void {
    this.notesPage = page;
    this.getNotes();
  }

  onNotesPageSizeChange(pageSize: number): void {
    this.notesPageSize = pageSize;
    this.notesPage = 1;
    this.getNotes();
  }

  handleNavItemClick(item: string): void {
    this.activeItem = item;
  }

  getResearchTopics(): void {
    this.isResearchTopicsLoading = true;
    this.apiService.getClientResearchTopics(this.clientId!).subscribe(
      (data: ResearchTopicByClientIdCardPagedResponse) => {
        this.researchTopics = data.list || [];
        this.isResearchTopicsLoading = false;
        this.researchTopics.sort((a: ResearchTopicByClientIdCard, b: ResearchTopicByClientIdCard) =>
          a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1
        );
      },
      (error: any) => {
        console.error(error);
      }
    );
  }

  getCountries(): void {
    this.apiService.getCountries().subscribe((countries: CountryDtoPagedResult) => {
      this.countries =
        countries.list?.sort((a, b) => {
          if (a.code === 'LV') return -1;
          if (b.code === 'LV') return 1;
          return 0;
        }) ?? [];
      this.isCountriesLoading = false;
    });
  }

  getClient(): void {
    this.apiService.getClient(this.clientId!).subscribe(
      (client: ClientCard) => {
        this.client = client;
        this.setNavItemsByClientType(client.clientType);
        this.isClientLoading = false;
        if (client.clientType !== 'Organization') {
          const storedSection = localStorage.getItem(`${KEY_LAST_ACTIVE_SECTION}_${client.clientType}`);
          this.initialActiveItem = storedSection && this.navItems.includes(storedSection) ? storedSection : 'general';
        } else {
          this.initialActiveItem = 'general';
          this.loadLegalEntityForBreadcrumbs(client.legalEntityId!);
        }
        this.activeItem = this.initialActiveItem;
        this.setBreadcrumbs();
      },
      (error: any) => {
        console.error(error);
      }
    );
  }

  private loadLegalEntityForBreadcrumbs(legalEntityId: string): void {
    this.apiService.getLegalEntity(legalEntityId).subscribe((legalEntity: LegalEntityCard) => {
      this.legalEntity = legalEntity;
      this.apiService.getLegalEntityForms().subscribe((forms: LegalEntityFormOption[]) => {
        this.legalEntityFormOptions = forms;
        this.setBreadcrumbs();
      });
    });
  }

  private getLegalEntityBreadcrumbTitle(): string {
    if (!this.legalEntity) {
      return this.client?.legalEntityName ?? '';
    }
    const typeLabel = this.legalEntityFormOptions.find(o => o.code === this.legalEntity?.type)?.name || '';
    const name = typeLabel ? `${this.legalEntity.name}, ${typeLabel}` : this.legalEntity.name;
    return `${name} (${this.legalEntity.registrationNumber})`;
  }

  setNavItemsByClientType(clientType: string): void {
    if (clientType === 'Individual') {
      this.navItems = this.navItemsIndividual;
    } else if (clientType === 'Organization') {
      this.navItems = this.navItemsOrganization;
    }
  }

  setBreadcrumbs() {
    if (this.client?.clientType === 'Organization') {
      this.breadcrumbs = [
        {
          title: 'components.breadcrumbs.organizationList',
          url: '/klienti/juridiskas-personas'
        },
        {
          title: this.getLegalEntityBreadcrumbTitle(),
          url: `/juridiska-persona/${this.client.legalEntityId}`
        },
        {
          title: `${this.client.fullName}${this.client.officialDocumentId ? ` (${this.client.officialDocumentId})` : ''}`,
          url: `/klients/${this.client.id}`
        }
      ];
    } else {
      this.breadcrumbs = [
        {
          title: 'components.breadcrumbs.clientList',
          url: '/klienti'
        },
        {
          title: `${this.client?.fullName}${this.client?.officialDocumentId ? ` (${this.client?.officialDocumentId})` : ''}`,
          url: `/klients/${this.client?.id}`
        }
      ];
    }
  }

  getReadersCard(): void {
    this.apiService.getReadersCard(this.clientId!).subscribe(
      (readersCard: any) => {
        this.readersCard = readersCard;
        this.isReadersCardLoading = false;
      },
      (error: any) => {
        console.error(error);
      }
    );
  }

  onSectionChanged(activeItem: string, confirmed: boolean): void {
    this.nextSection = activeItem;

    if (!confirmed) {
      this.initialActiveItem = this.activeItem;
      this.changeBack = !this.changeBack;
      this.confirmModalOpen = true;
    } else {
      this.activeItem = this.nextSection;
      this.initialActiveItem = this.activeItem;
      this.changeBack = !this.changeBack;
      this.hasFormChanged = false;
      localStorage.setItem(`${KEY_LAST_ACTIVE_SECTION}_${this.client?.clientType}`, this.activeItem);
    }
  }

  deleteClient(data: DeleteClientRequest) {
    this.isClientLoading = true;
    this.apiService.deleteClient(data).subscribe(
      () => {
        this.isClientLoading = false;
        this.toastService.showSuccess('deleteClientSuccess', '', {
          shouldTranslate: true
        });
        this.modalService.dismissAll();
        if (this.client?.clientType === 'Organization') {
          localStorage.setItem(KEY_LAST_ACTIVE_ORG_SECTION, 'officers');
          this.router.navigate(['/juridiska-persona', this.client.legalEntityId]);
        } else {
          this.router.navigate(['/klienti']);
        }
      },
      (error: any) => {
        this.isClientLoading = false;
        if (error?.status === 400) {
          this.toastService.showDanger('deleteClientActiveInfoRequest', '', { shouldTranslate: true, delay: 8000 });
        } else {
          this.toastService.showRequestError();
        }
        console.error('Error deleting client:', error);
      }
    );
  }

  onClientUpdated(updatedClient: ClientCard): void {
    this.client = updatedClient;
    this.setBreadcrumbs();
  }

  navigateToInfoRequest(): void {
    this.router.navigate([`/${this.clientId}/pakalpojumi/informacijas-pieprasijums`]);
  }
}
